import { useMemo } from 'react';
import { Button, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import type { PendingSignaturePlacementDto } from '@dealstack/shared/lib/generated';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useDownloadedFileBlobMutation } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadedFileBlobMutation';
import { PlacementEditorLayout } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/PlacementEditorLayout';
import type { SlotListItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/SlotList/SlotList.types';
import { usePendingSignaturePlacement } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/placement/usePendingSignaturePlacement';
import type { PendingSlot } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/placement/clientSideSlots.types';

interface Props {
	slots: PendingSlot[];
	fileId: string;
	dealId: string;
	showStampMetadata: boolean;
	onPlacementsChange: (placements: PendingSignaturePlacementDto[]) => void;
	onShowStampMetadataChange: (showStampMetadata: boolean) => void;
	/** Renders a Done button in the right-panel footer that closes the modal.
	 *  Placements are already published via onPlacementsChange on every change. */
	onClose?: () => void;
}

/**
 * Inline placement editor for SendDocumentModal: renders the uploaded PDF, lets
 * the admin place a stamp per client-enumerated slot, and publishes the derived
 * PendingSignaturePlacementDto[] to the parent on every change.
 */
export const PendingPlacementEditor = ({
	slots,
	fileId,
	dealId,
	showStampMetadata,
	onPlacementsChange,
	onShowStampMetadataChange,
	onClose,
}: Props) => {
	const { downloadedFileBlob } = useDownloadedFileBlobMutation();

	// useQuery, not useMutation+useEffect: the mutation hook's fn ref changes
	// every render, so an effect dep on it would loop infinitely.
	const blobQuery = useQuery({
		queryKey: ['sdw-placement-blob', dealId, fileId],
		queryFn: async () => {
			const data = await downloadedFileBlob({ fileId, dealId });
			return data.blob;
		},
		enabled: fileId.length > 0,
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		retry: false,
	});

	const {
		placementsByKey,
		slotByKey,
		selectedSlotKey,
		setSelectedSlotKey,
		onPlacementChange,
		onPlaceAtDefault,
		onRemove,
		isComplete,
		hasOverlappingPlacements,
	} = usePendingSignaturePlacement(slots, showStampMetadata, onPlacementsChange);

	const slotListItems = useMemo<SlotListItem[]>(
		() =>
			slots.map((slot) => ({
				key: slot.key,
				label: slot.label,
				isPlaced: placementsByKey.has(slot.key),
			})),
		[slots, placementsByKey]
	);

	if (slots.length === 0) {
		return (
			<Text size="sm" c="gray.7">
				This document has no signature roles configured.
			</Text>
		);
	}

	if (blobQuery.isLoading || (!blobQuery.data && !blobQuery.isError)) {
		return (
			<LoaderHost>
				<InlineLoader />
			</LoaderHost>
		);
	}

	if (blobQuery.isError || !blobQuery.data) {
		return (
			<Text size="sm" c="red">
				Failed to load the document. Please try again.
			</Text>
		);
	}

	return (
		<PlacementEditorLayout
			file={blobQuery.data}
			showMetadata={showStampMetadata}
			onShowMetadataChange={onShowStampMetadataChange}
			placements={placementsByKey}
			slotByKey={slotByKey}
			selectedSlotKey={selectedSlotKey}
			onSelect={setSelectedSlotKey}
			onPlacementChange={onPlacementChange}
			onRemove={onRemove}
			onPlaceAtDefault={onPlaceAtDefault}
			slotListItems={slotListItems}
			placedCount={placementsByKey.size}
			totalCount={slots.length}
			isComplete={isComplete}
			onBack={onClose}
			footer={
				onClose ? (
					<>
						<Button onClick={onClose} disabled={hasOverlappingPlacements}>
							Done
						</Button>
						<Button variant="default" onClick={onClose}>
							Back
						</Button>
					</>
				) : undefined
			}
		/>
	);
};

const LoaderHost = styled.div`
	position: relative;
	min-height: 240px;
`;
